import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * Dance floor — clear chibi sprites, correct scale (not a color blob).
 * Momoha-ish (pink twin buns), mint DJ, optional cover orb.
 */

type Kind = 'momoha' | 'mint' | 'cover';

type Dancer = {
  kind: Kind;
  phase: number;
  flip: number;
  bin: number;
  xN: number; // 0..1 stage x
};

const dancers: Dancer[] = [
  { kind: 'momoha', phase: 0.2, flip: 1, bin: 4, xN: 0.22 },
  { kind: 'mint', phase: 1.1, flip: -1, bin: 18, xN: 0.5 },
  { kind: 'momoha', phase: 2.4, flip: 1, bin: 32, xN: 0.78 },
  { kind: 'cover', phase: 0.7, flip: 1, bin: 48, xN: 0.36 },
  { kind: 'mint', phase: 3.0, flip: -1, bin: 56, xN: 0.64 },
];

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Local figure fits roughly in 0..100 height units, feet at y=0. */
function drawMomoha(
  ctx: CanvasRenderingContext2D,
  bounce: number,
  arm: number,
  squat: number,
  flip: number,
): void {
  ctx.save();
  ctx.scale(flip, 1);

  // shadow at feet
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.ellipse(0, 4 + squat * 0.3, 22, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.translate(0, -bounce - squat * 0.3);

  // legs (from hip ~ -28 to feet 0)
  ctx.strokeStyle = '#3a2038';
  ctx.lineWidth = 6;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-8, -28);
  ctx.lineTo(-11 - arm * 5, 0 + squat);
  ctx.moveTo(8, -28);
  ctx.lineTo(11 + arm * 5, 0 + squat);
  ctx.stroke();

  // shoes
  ctx.fillStyle = '#2a1528';
  ctx.beginPath();
  ctx.ellipse(-12 - arm * 5, 2 + squat, 8, 3.5, 0, 0, Math.PI * 2);
  ctx.ellipse(12 + arm * 5, 2 + squat, 8, 3.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // dress / body
  const body = ctx.createLinearGradient(0, -70, 0, -20);
  body.addColorStop(0, '#ff9ad0');
  body.addColorStop(1, '#e0509a');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(-18, -32);
  ctx.quadraticCurveTo(-22, -50, -12, -62);
  ctx.lineTo(12, -62);
  ctx.quadraticCurveTo(22, -50, 18, -32);
  ctx.closePath();
  ctx.fill();
  // skirt flare
  ctx.beginPath();
  ctx.moveTo(-18, -36);
  ctx.lineTo(-26, -22);
  ctx.lineTo(26, -22);
  ctx.lineTo(18, -36);
  ctx.closePath();
  ctx.fill();

  // head
  ctx.fillStyle = '#ffe8f4';
  ctx.beginPath();
  ctx.arc(0, -78, 18, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(200,80,140,0.35)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // hair base
  ctx.fillStyle = '#ff6eb4';
  ctx.beginPath();
  ctx.arc(0, -82, 17, Math.PI * 1.05, Math.PI * 1.95);
  ctx.fill();

  // twin buns
  ctx.fillStyle = '#ff5aad';
  ctx.beginPath();
  ctx.arc(-16, -96 - arm * 2, 11, 0, Math.PI * 2);
  ctx.arc(16, -96 + arm * 2, 11, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffb3d9';
  ctx.beginPath();
  ctx.arc(-16, -96 - arm * 2, 5, 0, Math.PI * 2);
  ctx.arc(16, -96 + arm * 2, 5, 0, Math.PI * 2);
  ctx.fill();

  // face
  ctx.fillStyle = '#2a1830';
  ctx.beginPath();
  ctx.arc(-6, -80, 2.4, 0, Math.PI * 2);
  ctx.arc(6, -80, 2.4, 0, Math.PI * 2);
  ctx.fill();
  // blush
  ctx.fillStyle = 'rgba(255,120,160,0.35)';
  ctx.beginPath();
  ctx.ellipse(-11, -74, 4, 2.2, 0, 0, Math.PI * 2);
  ctx.ellipse(11, -74, 4, 2.2, 0, 0, Math.PI * 2);
  ctx.fill();
  // smile
  ctx.strokeStyle = '#c44a88';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(0, -72, 5, 0.15, Math.PI - 0.15);
  ctx.stroke();

  // arms
  ctx.strokeStyle = '#ffe8f4';
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-14, -55);
  ctx.lineTo(-30, -48 - arm * 22);
  ctx.moveTo(14, -55);
  ctx.lineTo(30, -42 + arm * 22);
  ctx.stroke();
  // hands
  ctx.fillStyle = '#ffe8f4';
  ctx.beginPath();
  ctx.arc(-30, -48 - arm * 22, 4, 0, Math.PI * 2);
  ctx.arc(30, -42 + arm * 22, 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawMint(
  ctx: CanvasRenderingContext2D,
  bounce: number,
  arm: number,
  squat: number,
  flip: number,
): void {
  ctx.save();
  ctx.scale(flip, 1);

  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.ellipse(0, 4 + squat * 0.3, 20, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.translate(0, -bounce - squat * 0.3);

  ctx.strokeStyle = '#143830';
  ctx.lineWidth = 6;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-7, -28);
  ctx.lineTo(-10 - arm * 4, 0 + squat);
  ctx.moveTo(7, -28);
  ctx.lineTo(10 + arm * 4, 0 + squat);
  ctx.stroke();

  ctx.fillStyle = '#0c281f';
  ctx.beginPath();
  ctx.ellipse(-11 - arm * 4, 2 + squat, 7, 3, 0, 0, Math.PI * 2);
  ctx.ellipse(11 + arm * 4, 2 + squat, 7, 3, 0, 0, Math.PI * 2);
  ctx.fill();

  const body = ctx.createLinearGradient(0, -65, 0, -20);
  body.addColorStop(0, '#7dffe0');
  body.addColorStop(1, '#1aa88a');
  ctx.fillStyle = body;
  roundRect(ctx, -16, -60, 32, 36, 12);
  ctx.fill();

  // head
  ctx.fillStyle = '#e8fff8';
  ctx.beginPath();
  ctx.arc(0, -76, 16, 0, Math.PI * 2);
  ctx.fill();

  // headphones
  ctx.strokeStyle = '#0a201c';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(0, -76, 14, Math.PI * 1.15, Math.PI * 1.85);
  ctx.stroke();
  ctx.fillStyle = '#0a201c';
  roundRect(ctx, -18, -84, 6, 14, 2);
  ctx.fill();
  roundRect(ctx, 12, -84, 6, 14, 2);
  ctx.fill();

  ctx.fillStyle = '#0a201c';
  ctx.beginPath();
  ctx.arc(-5, -78, 2.2, 0, Math.PI * 2);
  ctx.arc(5, -78, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#1aa88a';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(0, -70, 4, 0.2, Math.PI - 0.2);
  ctx.stroke();

  ctx.strokeStyle = '#e8fff8';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(-16, -48);
  ctx.lineTo(-28, -40 - arm * 20);
  ctx.moveTo(16, -48);
  ctx.lineTo(28, -36 + arm * 20);
  ctx.stroke();

  ctx.restore();
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  cover: HTMLImageElement | null,
  bounce: number,
  arm: number,
  squat: number,
): void {
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.ellipse(0, 4 + squat * 0.3, 18, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.translate(0, -bounce - squat * 0.3);

  const s = 44 + arm * 4;
  // little stick legs
  ctx.strokeStyle = 'rgba(200,240,230,0.85)';
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-8, -8);
  ctx.lineTo(-10, 0 + squat);
  ctx.moveTo(8, -8);
  ctx.lineTo(10, 0 + squat);
  ctx.stroke();

  if (cover && cover.complete && cover.naturalWidth > 0) {
    ctx.save();
    roundRect(ctx, -s / 2, -s - 8, s, s, 10);
    ctx.clip();
    ctx.drawImage(cover, -s / 2, -s - 8, s, s);
    ctx.restore();
    ctx.strokeStyle = 'rgba(86,240,200,0.85)';
    ctx.lineWidth = 2.5;
    roundRect(ctx, -s / 2, -s - 8, s, s, 10);
    ctx.stroke();
  } else {
    ctx.fillStyle = '#56f0c8';
    roundRect(ctx, -s / 2, -s - 8, s, s, 10);
    ctx.fill();
    ctx.fillStyle = '#04120e';
    ctx.font = '800 16px ui-sans-serif, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('BC', 0, -s / 2 - 8);
  }
  ctx.restore();
}

export const dancersViz: Visualizer = {
  id: 'dancers',
  label: 'Dance Floor',
  draw(frame) {
    const { ctx, w, h, bands, freq, t, cover } = frame;
    clearBg(ctx, w, h, 0.5);

    const floorY = h * 0.78;

    // backdrop
    const sky = ctx.createLinearGradient(0, 0, 0, floorY);
    sky.addColorStop(0, '#0a1020');
    sky.addColorStop(1, '#121028');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, floorY);

    // simple stage lights (2 cones only — cheap)
    for (let i = 0; i < 2; i++) {
      const lx = w * (0.3 + i * 0.4);
      const g = ctx.createRadialGradient(lx, h * 0.08, 4, lx, floorY * 0.7, Math.min(w, h) * 0.35);
      const hue = i === 0 ? 320 : 165;
      g.addColorStop(0, `hsla(${hue}, 90%, 60%, ${0.1 + bands.beat * 0.12})`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, floorY);
    }

    // floor
    ctx.fillStyle = '#0a0e14';
    ctx.fillRect(0, floorY, w, h - floorY);
    ctx.strokeStyle = `rgba(86,240,200,${0.1 + bands.bass * 0.15})`;
    ctx.lineWidth = 1;
    for (let i = 0; i <= 8; i++) {
      const u = i / 8;
      const y = floorY + u * u * (h - floorY);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    // center stage line
    ctx.strokeStyle = `rgba(255,140,200,${0.15 + bands.energy * 0.2})`;
    ctx.beginPath();
    ctx.moveTo(w * 0.1, floorY);
    ctx.lineTo(w * 0.9, floorY);
    ctx.stroke();

    // waveform backdrop (single stroke)
    ctx.beginPath();
    const step = Math.max(2, Math.floor(frame.wave.length / 64));
    for (let i = 0; i < frame.wave.length; i += step) {
      const x = (i / frame.wave.length) * w;
      const y = floorY - 20 - Math.abs(frame.wave[i] || 0) * 50 * (0.6 + bands.energy);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = `rgba(86,240,200,${0.2 + bands.mids * 0.3})`;
    ctx.lineWidth = 2;
    ctx.stroke();

    // how many dancers
    const count = bands.energy > 0.55 ? 5 : bands.energy > 0.3 ? 4 : 3;
    // figure target height in px
    const figH = Math.min(w, h) * 0.32;
    const unit = figH / 100; // local units → px

    for (let i = 0; i < count; i++) {
      const d = dancers[i]!;
      const f = freq[d.bin % freq.length]! / 255;
      // smooth dance motion — not random jitter
      const tempo = 5 + bands.energy * 3;
      const bounce =
        (0.5 + 0.5 * Math.sin(t * tempo + d.phase)) * (8 + bands.kick * 22 + bands.bass * 12 + f * 8);
      const arm = Math.sin(t * (tempo * 0.85) + d.phase * 1.7);
      const squat = Math.max(0, Math.sin(t * tempo * 0.5 + d.phase)) * bands.bass * 8;
      const x = d.xN * w;
      const y = floorY;

      ctx.save();
      ctx.translate(x, y);
      ctx.scale(unit * d.flip, unit);
      // lean gently
      ctx.rotate(Math.sin(t * 1.5 + d.phase) * 0.06 * (0.4 + bands.mids));

      // draw with flip already in scale x — pass flip=1 to drawers
      if (d.kind === 'momoha') drawMomoha(ctx, bounce, arm, squat, 1);
      else if (d.kind === 'mint') drawMint(ctx, bounce, arm, squat, 1);
      else drawCover(ctx, cover, bounce, arm, squat);

      ctx.restore();
    }

    // name tags small
    ctx.font = `600 ${Math.max(10, Math.round(w * 0.012))}px ui-sans-serif, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillStyle = `rgba(255,200,230,${0.45 + bands.energy * 0.3})`;
    if (count >= 1) ctx.fillText('Momoha', dancers[0]!.xN * w, floorY + 18);
    if (count >= 2) ctx.fillText('Mint', dancers[1]!.xN * w, floorY + 18);

    drawHud(frame);
  },
};
